import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { COLLECTION_NAME } from "../constants/endpoints";

/**
 * Fare used as a refund cap from ride documents.
 * For cancelled rides, UI "Retained Price" is `price` — prefer it over `originalPrice`
 * so we don't subtract partial refunds against the full original and show $0 incorrectly.
 */
export const getChargeableRideFare = (rideLike) => {
  if (!rideLike || typeof rideLike !== "object") return 0;

  const hasCancellation =
    "cancellationStatus" in rideLike || !!rideLike.cancellationStatus;

  const original = parseFloat(rideLike.originalPrice);
  const price = parseFloat(rideLike.price);
  const totalRideFare = parseFloat(rideLike.total_ride_fare);
  const refundAmount = parseFloat(rideLike.refundAmount);

  if (hasCancellation && Number.isFinite(price) && price > 0) {
    return price;
  }
  if (Number.isFinite(original) && original > 0) return original;
  if (Number.isFinite(price) && price > 0) return price;
  if (Number.isFinite(totalRideFare) && totalRideFare > 0) return totalRideFare;
  if (Number.isFinite(refundAmount) && refundAmount > 0) return refundAmount;
  return 0;
};

/**
 * Remaining refundable amount from payment rows, capped by ride-document fare.
 *
 * @param {object} [options]
 * @param {boolean} [options.useRideHintIfNoNet] — cancelled rides only: if net from payments
 *   is <= 0 but the ledger is not “fully refunded”, may use fare hint (ledger lag).
 *   Never applies when refunds cover what was collected (payment already settled).
 * @param {number} [options.tipAmount] — tip on the ride (passenger → driver); excluded from
 *   passenger-fare refunds and removed from summed PAYMENT totals when present.
 */
export const calculateAllowableRefund = async (
  rideId,
  totalFareHint = 0,
  options = {}
) => {
  const { useRideHintIfNoNet = false } = options;
  const tipAmount = Math.max(
    0,
    Number.parseFloat(String(options.tipAmount ?? 0)) || 0
  );

  if (!rideId) {
    return "0.00";
  }

  try {
    const q = query(
      collection(db, COLLECTION_NAME.payments),
      where("rideId", "==", rideId)
    );

    const snapshot = await getDocs(q);

    let totalPaid = 0;
    let totalRefunded = 0;

    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      const amount = Math.abs(Number(data.amount || 0));
      const paymentType = String(data.type || "").toUpperCase();
      if (!Number.isFinite(amount) || amount <= 0) return;

      if (paymentType === "PAYMENT") {
        totalPaid += amount;
      } else if (paymentType === "REFUND") {
        totalRefunded += amount;
      }
    });

    const paidExcludingTips = Math.max(0, totalPaid - tipAmount);
    const netFromPayments = paidExcludingTips - totalRefunded;

    // No fare on the ride document → do not infer passenger refundable amount from
    // payment rows alone (avoids e.g. $40 net payments when retained/driver/swyft are all 0).
    if (!(totalFareHint > 0)) {
      return "0.00";
    }

    let allowable = Math.max(0, netFromPayments);

    allowable = Math.min(allowable, totalFareHint);

    if (
      allowable <= 0 &&
      totalFareHint > 0 &&
      totalPaid === 0 &&
      totalRefunded === 0
    ) {
      allowable = totalFareHint;
    }

    const passengerCollected = paidExcludingTips;
    const ledgerShowsFullyRefunded =
      passengerCollected > 0 &&
      totalRefunded >= passengerCollected - 1e-6;

    if (
      useRideHintIfNoNet &&
      allowable <= 0 &&
      totalFareHint > 0 &&
      netFromPayments <= 0 &&
      !ledgerShowsFullyRefunded
    ) {
      allowable = totalFareHint;
    }

    return allowable > 0 ? allowable.toFixed(2) : "0.00";
  } catch (error) {
    console.error("Error calculating allowable refund:", error);
    return null;
  }
};
